import React, { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AccrualScheduleBuilder, newTier } from '../components/timeoff/AccrualScheduleBuilder';
import { ArrowLeftIcon, CheckIcon, ChevronDownIcon, SaveIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TextField } from '../components/ui/TextField';
import { Toggle } from '../components/ui/Toggle';
import { departmentNames } from '../data/org';
import { useHRStore } from '../hooks/useHRStore';
import type { AccrualFrequency, AccrualTier, AnniversaryMode, Department, Navigate, PolicyForm, PolicyType } from '../types/hr';
import { hrActions } from '../utils/hrStore';
import { buildPolicy, describePolicy, hasSchedule, sortedTiers, tierCarryover } from '../utils/policy';
const BLANK: PolicyForm = {
  name: '',
  type: 'Accrual',
  accrualRate: '',
  accrualFrequency: 'Monthly',
  waitingDays: '0',
  prorate: true,
  yearOneCap: '',
  anniversaryMode: 'top-up',
  anniversaryAmount: '',
  carryover: '',
  departments: []
};
const TYPES: {
  id: PolicyType;
  hint: string;
}[] = [{
  id: 'Accrual',
  hint: 'Earn over time'
}, {
  id: 'Fixed grant',
  hint: 'Lump sum each year'
}, {
  id: 'Unlimited',
  hint: 'Tracked, no balance'
}];
const FREQUENCIES: AccrualFrequency[] = ['Monthly', 'Per pay period', 'Annually'];
const ANNIVERSARY_MODES: {
  id: AnniversaryMode;
  title: string;
  hint: string;
}[] = [{
  id: 'top-up',
  title: 'Top up to X days',
  hint: 'Bring balance up to a target'
}, {
  id: 'add',
  title: 'Add X days',
  hint: 'Grant a flat amount on top'
}];
export function PolicyEditor({
  policyId,
  onNavigate
}: {
  policyId?: string;
  onNavigate: Navigate;
}) {
  const {
    employees,
    policies
  } = useHRStore();
  const existing = policies.find(p => p.id === policyId);
  const [form, setForm] = useState<PolicyForm>(() => {
    const source = existing ? existing.form : BLANK;
    return {
      ...BLANK,
      ...source,
      departments: [...(source.departments ?? [])],
      schedule: source.schedule?.map(t => ({
        ...t
      }))
    };
  });
  const [submitted, setSubmitted] = useState(false);
  const savedSchedule = useRef<AccrualTier[] | undefined>(form.schedule);
  const scheduled = hasSchedule(form);
  const setMethod = (method: 'flat' | 'schedule') => {
    if (method === 'schedule' && !scheduled) {
      set('schedule', savedSchedule.current?.length ? savedSchedule.current : [{
        ...newTier('0'),
        id: 'tier-start',
        days: form.accrualRate
      }]);
    }
    if (method === 'flat' && scheduled) {
      savedSchedule.current = form.schedule;
      set('schedule', undefined);
    }
  };
  function set<K extends keyof PolicyForm>(key: K, value: PolicyForm[K]) {
    setForm(f => ({
      ...f,
      [key]: value
    }));
  }
  const nameError = submitted && !form.name.trim() ? 'Give the policy a name' : undefined;
  const matched = employees.filter(e => form.departments.includes(e.department) && e.status !== 'Terminated').length;
  const amount = Number(form.anniversaryAmount) || 0;
  const preview = describePolicy(form);
  const cancel = () => onNavigate(existing ? {
    page: 'policy',
    policyId: existing.id
  } : {
    page: 'timeoff',
    tab: 'policies'
  });
  const save = () => {
    setSubmitted(true);
    if (!form.name.trim()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    const id = existing ? existing.id : `policy-${Date.now()}`;
    hrActions.savePolicy(buildPolicy(existing, form, id));
    toast.success(existing ? 'Policy updated' : 'Policy created', {
      description: `${form.name.trim()} saved — the summary and policy card now reflect these rules.`
    });
    onNavigate({
      page: 'policy',
      policyId: id
    });
  };
  const toggleDept = (d: Department) => set('departments', form.departments.includes(d) ? form.departments.filter(x => x !== d) : [...form.departments, d]);
  return <div className="pb-24" data-id="element-1089" data-mp="1vf4y44j007we">
      <button type="button" onClick={cancel} className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted transition hover:text-navy" data-id="element-1090" data-mp="1vfy3b12b6pcf">
        <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1091" data-mp="21v4zuwjucd4g" /> {existing ? existing.name : 'Policies'}
      </button>
      <div className="mb-8" data-id="element-1092" data-mp="26u85oj7nandh">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-1093" data-mp="26v9k8xnvdrzy">{existing ? 'Edit policy' : 'New policy'}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-1094" data-mp="2bsji91pbrbze">{existing ? `Edit ${existing.name}` : 'Create a time off policy'}</h1>
        <p className="mt-1.5 text-sm text-muted" data-id="element-1095" data-mp="1j3vsotxjm0g1">Leave a value blank to keep it marked as TBD on the policy summary.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1096" data-mp="1o25pta2tvb4a">
        <div className="space-y-6" data-id="element-1097" data-mp="1sv8jrjbioml0">
          <Section step={1} title="Basics" data-id="element-1098" data-mp="1swjk195r7c7d">
            <TextField label="Policy name" value={form.name} onChange={v => set('name', v)} placeholder="e.g. Regular PTO" error={nameError} data-id="element-1099" data-mp="vfumvsrr0y4b" />
            <div className="mt-5" data-id="element-1100" data-mp="10c2y4v2ts767">
              <p className="text-xs font-medium text-slate-600" data-id="element-1101" data-mp="sud50sfz6jix">Policy type</p>
              <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-1102" data-mp="ssahcf2ap2hd">
                {TYPES.map(t => <ChoiceCard key={t.id} selected={form.type === t.id} onClick={() => set('type', t.id)} title={t.id} hint={t.hint} data-id="element-1103" data-mp="iw768jpgttia" />)}
              </div>
            </div>
          </Section>

          <Section step={2} title="Accrual & eligibility" data-id="element-1104" data-mp="9wan0xluhmum">
            <p className="text-xs font-medium text-slate-600" data-id="element-1105" data-mp="9xvj4rnt5dul">Accrual method</p>
            <div className="mb-5 mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-1106" data-mp="53hjue60ymkn">
              <ChoiceCard selected={!scheduled} onClick={() => setMethod('flat')} title="Flat rate" hint="Same rate for everyone, with an anniversary rule" data-id="element-1107" data-mp="70tddbcrw4e" />
              <ChoiceCard selected={scheduled} onClick={() => setMethod('schedule')} title="Milestone schedule" hint="Rate steps up with years of service" data-id="element-1108" data-mp="3md62rta3fadn" />
            </div>
            {scheduled ? <AccrualScheduleBuilder tiers={form.schedule ?? []} waitingDays={form.waitingDays} onChange={tiers => set('schedule', tiers)} onWaitingChange={v => set('waitingDays', v)} data-id="element-1109" data-mp="37qqmmw6ds63z" /> : <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-1110" data-mp="33olvq57bhnl1">
              <TextField label="Accrual rate" value={form.accrualRate} onChange={v => set('accrualRate', v)} suffix="days" inputMode="decimal" placeholder="TBD" data-id="element-1111" data-mp="33qzo26am4low" />
              <div data-id="element-1112" data-mp="3vf03rksng5os">
                <label htmlFor="frequency" className="block text-xs font-medium text-slate-600" data-id="element-1113" data-mp="3vfjiytqx0fz4">
                  Frequency
                </label>
                <div className="relative mt-1.5" data-id="element-1114" data-mp="3mnhm15g21htl">
                  <select id="frequency" value={form.accrualFrequency} onChange={e => set('accrualFrequency', e.target.value as AccrualFrequency)} className="h-11 w-full appearance-none rounded-xl border-0 bg-white px-3.5 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-1115" data-mp="3mlwu7nl4kczf">
                    {FREQUENCIES.map(f => <option key={f} data-id="element-1116" data-mp="12283byr1uuzz">{f}</option>)}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" data-id="element-1117" data-mp="x57y1q20xw9x" />
                </div>
              </div>
              <TextField label="Waiting period" value={form.waitingDays} onChange={v => set('waitingDays', v)} suffix="days" inputMode="numeric" data-id="element-1118" data-mp="5da7sebh6t1r" />
            </div>
            <div className="mt-5 flex items-center justify-between rounded-xl bg-canvas px-4 py-3" data-id="element-1119" data-mp="jaozjpp7bcww">
              <div data-id="element-1120" data-mp="efrwczelrxrx">
                <p className="text-sm font-medium text-navy" data-id="element-1121" data-mp="1uryhu8wlfywm">Prorate first period</p>
                <p className="text-xs text-muted" data-id="element-1122" data-mp="1utrr7vylcx8b">Partial months accrue by days employed</p>
              </div>
              <Toggle checked={form.prorate} onChange={v => set('prorate', v)} label="Prorate first period" srOnlyLabel data-id="element-1123" data-mp="3k92ie50hy03r" />
            </div>
            </>}
          </Section>

          {!scheduled && <>
          <Section step={3} title="Caps & carryover" data-id="element-1124" data-mp="3t9ikpb5i7171">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-1125" data-mp="3t7xl374c0s2b">
              <TextField label="Year-one cap" value={form.yearOneCap} onChange={v => set('yearOneCap', v)} suffix="days" placeholder="TBD" hint="Accrual pauses at this balance" data-id="element-1126" data-mp="1wns6rj7apcj" />
              <TextField label="Carryover limit" value={form.carryover} onChange={v => set('carryover', v)} suffix="days" placeholder="TBD" hint="Max balance carried into next year" data-id="element-1127" data-mp="bs7q1jhbry3x" />
            </div>
          </Section>

          <Section step={4} title="Anniversary rule" data-id="element-1128" data-mp="kt5u21rrk5mi">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-1129" data-mp="2hmqrupkimkts">
              {ANNIVERSARY_MODES.map(m => <ChoiceCard key={m.id} selected={form.anniversaryMode === m.id} onClick={() => set('anniversaryMode', m.id)} title={m.title} hint={m.hint} data-id="element-1130" data-mp="2mmd5v186453n" />)}
            </div>
            <div className="mt-5 grid grid-cols-1 items-end gap-5 sm:grid-cols-2" data-id="element-1131" data-mp="2aasw6410eili">
              <TextField label="X (days)" value={form.anniversaryAmount} onChange={v => set('anniversaryAmount', v)} suffix="days" placeholder="TBD" data-id="element-1132" data-mp="25bgcfs5fprn5" />
              <p className="rounded-xl bg-flame/[0.06] px-4 py-3 text-xs leading-relaxed text-slate-600 ring-1 ring-inset ring-flame/15" data-id="element-1133" data-mp="20epvz3ppwu17">
                {amount > 0 ? form.anniversaryMode === 'top-up' ? <>
                      Example: 10 days banked → <span className="font-semibold text-navy" data-id="element-1134" data-mp="2o163q59znvab">+{Math.max(0, amount - 10)} days</span> → {Math.max(10, amount)}
                    </> : <>
                      Example: 10 days banked → <span className="font-semibold text-navy" data-id="element-1135" data-mp="1cl7plmzyej28">+{amount} days</span> → {10 + amount}
                    </> : 'Enter X to preview how the rule applies.'}
              </p>
            </div>
          </Section>

          </>}

          <Section step={scheduled ? 3 : 5} title="Assign employees" data-id="element-1136" data-mp="2slpanf7wgw58">
            <p className="text-xs text-muted" data-id="element-1137" data-mp="2sn0h5msqci01">Choose departments to enroll. Individuals can be added from their profile.</p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-1138" data-mp="32jv9fnyetm5v">
              {departmentNames.map(d => {
              const on = form.departments.includes(d);
              return <button key={d} type="button" aria-pressed={on} onClick={() => toggleDept(d)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 ring-inset transition ${on ? 'bg-navy text-white ring-navy' : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'}`} data-id="element-1139" data-mp="2xn4vb66610n3">
                    {on && <CheckIcon className="h-3.5 w-3.5" data-id="element-1140" data-mp="3nwvtwu0apcsr" />}
                    {d}
                  </button>;
            })}
            </div>
            <p className="mt-4 text-sm text-slate-600" data-id="element-1141" data-mp="3f1fwy448uhiy">
              <span className="font-semibold text-navy" data-id="element-1142" data-mp="3j42pk78qo6h7">{matched}</span> sample employees work in the selected departments.
            </p>
          </Section>
        </div>

        <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start" data-id="element-1143" data-mp="353033tdu0u5r">
          <section className="rounded-2xl bg-navy p-6 text-white shadow-lift" aria-live="polite" data-id="element-1144" data-mp="3a47ahrc2brbw">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-1145" data-mp="2w4y6rr51mpot">Rule preview</p>
            <p className="mt-3 text-[15px] leading-relaxed" data-id="element-1146" data-mp="305zzdp4gg3ht">{preview.sentence}</p>
            {preview.tbd.length > 0 ? <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-1147" data-mp="ua5kfslxiwo6">
                {preview.tbd.map(t => <span key={t} className="rounded-full bg-amber-400/15 px-2.5 py-0.5 text-[11px] font-medium text-amber-300 ring-1 ring-inset ring-amber-400/30" data-id="element-1148" data-mp="ybit0agepdys">
                    {t}: TBD
                  </span>)}
              </div> : <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-emerald-300" data-id="element-1149" data-mp="1yanf8jizdq2c">
                <CheckIcon className="h-3.5 w-3.5" data-id="element-1150" data-mp="1tcc3g394l5hh" /> All rules defined
              </p>}
          </section>
          <Card title="Live summary" data-id="element-1151" data-mp="2q0i8usdvg1wt">
            <ul className="space-y-3 text-sm" data-id="element-1152" data-mp="2lzy5h72pg228">
              <SummaryRow label="Name" value={form.name} data-id="element-1153" data-mp="2m07xf07b8jak" />
              <SummaryRow label="Type" value={form.type} data-id="element-1154" data-mp="2h8gar0r9yxpp" />
              {scheduled ? <>
                  {sortedTiers(form.schedule ?? []).map((t, i) => <SummaryRow key={t.id} label={i === 0 ? 'Starting' : `After ${t.afterYears || '?'} yrs`} value={t.days ? `${t.days} days / ${t.frequency === 'Yearly' ? 'yr' : 'mo'}` : ''} data-id="element-1155" data-mp="39p0nwh55xyxs" data-mpi={i} />)}
                  <SummaryRow label="Waiting period" value={Number(form.waitingDays) ? `${form.waitingDays} days` : 'None'} data-id="element-1156" data-mp="3ca8e7pg8tybd" />
                  <SummaryRow label="Carryover" value={form.schedule?.[0] && tierCarryover(form.schedule[0]) !== 'TBD' ? tierCarryover(form.schedule[0]) : ''} data-id="element-1157" data-mp="3h9l0a7zaj1cw" />
                </> : <>
                  <SummaryRow label="Accrual" value={form.accrualRate ? `${form.accrualRate} days · ${form.accrualFrequency.toLowerCase()}` : ''} data-id="element-1158" data-mp="3qasykq1t426z" />
                  <SummaryRow label="Waiting period" value={form.waitingDays ? `${form.waitingDays} days` : ''} data-id="element-1159" data-mp="41qbeiawwjb3" />
                  <SummaryRow label="Proration" value={form.prorate ? 'On' : 'Off'} data-id="element-1160" data-mp="8sq8pony13e4" />
                  <SummaryRow label="Year-one cap" value={form.yearOneCap ? `${form.yearOneCap} days` : ''} data-id="element-1161" data-mp="dskxnmo5riwj" />
                  <SummaryRow label="Anniversary" value={amount ? form.anniversaryMode === 'top-up' ? `Top up to ${amount}` : `Add ${amount}` : ''} data-id="element-1162" data-mp="hug04uhts2bw" />
                  <SummaryRow label="Carryover" value={form.carryover ? `${form.carryover} days` : ''} data-id="element-1163" data-mp="mqoi0fneq9mr" />
                </>}
              <SummaryRow label="Departments" value={form.departments.length ? `${form.departments.length} selected` : ''} data-id="element-1164" data-mp="54ywxwsa83jr" />
            </ul>
          </Card>
        </aside>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/90 backdrop-blur-md lg:left-72" data-id="element-1165" data-mp="dx0u9m7213pt">
        <div className="flex items-center justify-end gap-3 px-6 py-4 lg:px-10" data-id="element-1166" data-mp="3mbsnmy73i28s">
          <span className="mr-auto hidden text-xs text-muted sm:block" data-id="element-1167" data-mp="3ib8q3tim0wm4">Unsaved changes are discarded on cancel.</span>
          <Button onClick={cancel} data-id="element-1168" data-mp="5m7r5dxuke0">Cancel</Button>
          <Button variant="primary" icon={SaveIcon} onClick={save} data-id="element-1169" data-mp="39is89rj9rg">
            Save policy
          </Button>
        </div>
      </div>
    </div>;
}
function Section({
  step,
  title,
  children
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  return <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-1170" data-mp="16kmvinst7w9i">
      <div className="mb-5 flex items-center gap-3" data-id="element-1171" data-mp="2ohzzr8a0yj49">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-xs font-semibold text-white" data-id="element-1172" data-mp="2ti4jd768btyp">{step}</span>
        <h2 className="text-[15px] font-semibold text-navy" data-id="element-1173" data-mp="2fj4vw2ugdl44">{title}</h2>
      </div>
      {children}
    </section>;
}
function ChoiceCard({
  selected,
  onClick,
  title,
  hint
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  hint: string;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} className={`flex items-start gap-3 rounded-xl px-4 py-3 text-left ring-1 ring-inset transition ${selected ? 'bg-azure/[0.06] ring-2 ring-azure' : 'bg-white ring-slate-200 hover:ring-slate-300'}`} data-id="element-1174" data-mp="389ekj7jeo948">
      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${selected ? 'bg-azure ring-azure' : 'ring-slate-300'}`} data-id="element-1175" data-mp="33dz88k11ql9x">
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-1176" data-mp="3vypmutgembkh" />}
      </span>
      <span data-id="element-1177" data-mp="9lwdl7nfnvwa">
        <span className="block text-sm font-medium text-navy" data-id="element-1178" data-mp="9mfuqm59eg99">{title}</span>
        <span className="block text-xs text-muted" data-id="element-1179" data-mp="4p5t884lie52">{hint}</span>
      </span>
    </button>;
}
function SummaryRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <li className="flex items-center justify-between gap-3" data-id="element-1180" data-mp="3qw453cv9hvqn">
      <span className="text-muted" data-id="element-1181" data-mp="3qxoyv09r5ang">{label}</span>
      {value ? <span className="truncate text-right font-medium text-navy" data-id="element-1182" data-mp="3mvahylpn8kyt">{value}</span> : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-500/30" data-id="element-1183" data-mp="3i290q71ys9c2">TBD</span>}
    </li>;
}